"use client";

import { useId, useMemo, useState } from "react";
import { Heading, HeadingProps } from "@repo/ui/heading";
import {
  BlocksRenderer,
  type BlocksContent,
} from "@strapi/blocks-react-renderer";

interface ListItem {
  id: number;
  title: string;
  description: BlocksContent;
}

interface Headline {
  heading: HeadingProps;
  description?: BlocksContent | null;
}

interface ListItemsProps {
  headline: Headline;
  items: ListItem[];
  /** How many items to show before “Show more” (default: 4) */
  initialCount?: number;
}

export default function ListItems({
  headline,
  items,
  initialCount = 4,
}: ListItemsProps) {
  if (!headline?.heading?.title || !items?.length) return null;

  const [expanded, setExpanded] = useState(false);
  const listId = `rules-list-${useId()}`;

  const visibleItems = useMemo(
    () => (expanded ? items : items.slice(0, initialCount)),
    [expanded, items, initialCount],
  );

  const canToggle = items.length > initialCount;
  const remaining = Math.max(0, items.length - initialCount);

  return (
    <section
      className="max-w-7xl reveal-stagger mx-auto relative md:py-14 py-10 flex flex-col gap-10 w-full"
      aria-labelledby="house-rules-title"
    >
      <Heading
        id="house-rules-title"
        level={headline.heading.level}
        className="xl:text-7xl md:text-6xl text-4xl font-noto reveal"
      >
        {headline.heading.title}
      </Heading>

      {headline.description && (
        <div className="max-w-xl text-grey-100 reveal-item">
          <BlocksRenderer content={headline.description} />
        </div>
      )}

      <ul
        id={listId}
        className="flex flex-col gap-6 max-w-xl"
        role="list"
        aria-label="List of items"
      >
        {visibleItems.map((item) => (
          <li
            key={item.id}
            className="flex flex-col gap-1.5 border-b border-gray-200 pb-6 reveal-item"
            role="listitem"
          >
            <Heading level="h5" className="md:text-2xl text-xl reveal-item">
              {item.title}
            </Heading>
            {item.description && (
              <div className="text-sm text-grey-100 reveal-item">
                <BlocksRenderer content={item.description} />
              </div>
            )}
          </li>
        ))}
      </ul>

      {canToggle && (
        <div>
          <button
            type="button"
            aria-controls={listId}
            aria-expanded={expanded}
            onClick={() => setExpanded((v) => !v)}
            className="mt-2 cursor-pointer reveal-item inline-flex items-center gap-2 border border-grey-300 px-5 py-2 text-sm uppercase hover:bg-primary hover:text-white transition"
          >
            {expanded
              ? "Show less"
              : `Show more${remaining ? ` (+${remaining})` : ""}`}
          </button>
        </div>
      )}
    </section>
  );
}
